import { ListItemIcon, MenuItem } from "@material-ui/core";
import PropTypes from "prop-types";
import React, { Fragment, useState } from "react";
import {
	Button,
	Confirm,
	useCreate,
	useNotify,
	useTranslate,
} from "react-admin";

/**
 *
 * Pulsante generico per aggiungere un comando
 * alla coda di esecuzione
 *
 * @example
 *
 * <CommandButton
 * 		icon={<AccountBalanceWalletIcon />}
 *    label={translate("resources.languages.actions.search")}
 *	  notifySuccess={translate("resources.languages.actions.notify_success")}
 *	  confirmTitle={translate("resources.languages.actions.confirm_title")}
 *    confirmContent={translate("resources.languages.actions.confirm_content")}
 *	  command={{
 *		  name: "language",
 *		  args: ["search", "test"],
 *		  notify_args: {
 *			  title: translate("resources.languages.actions.title"),
 *			  content: translate("resources.languages.actions.content"),
 *			  resource: "notifications",
 *		  },
 *	  }}
 * />;
 *
 */

const CommandButton = ({
	record,
	resource,
	basePath,
	isButton,
	command,
	label,
	icon,
	className,
	notifySuccess,
	confirmTitle,
	confirmContent,
	...props
}) => {
	const translate = useTranslate();
	const notify = useNotify();
	const [open, setOpen] = useState(false);
	const handleClick = () => setOpen(true);
	const handleDialogClose = () => setOpen(false);
	const [create, { loading }] = useCreate("commands", command, {
		onSuccess: () => {
			notify(notifySuccess);
		},
	});
	const handleConfirm = () => {
		create();
		setOpen(false);
	};
	const translatedLabel = translate(label);

	return (
		<Fragment>
			{isButton ? (
				<Button
					disabled={loading}
					color="secondary"
					label={translatedLabel}
					onClick={handleClick}
					disableElevation
					className={className}
					{...props}
				>
					{icon}
				</Button>
			) : (
				<MenuItem onClick={handleClick}>
					<ListItemIcon>{icon}</ListItemIcon>
					{translatedLabel}
				</MenuItem>
			)}
			<Confirm
				isOpen={open}
				loading={loading}
				title={translate(confirmTitle)}
				content={translate(confirmContent)}
				onConfirm={handleConfirm}
				onClose={handleDialogClose}
			/>
		</Fragment>
	);
};

CommandButton.defaultProps = {
	isButton: false,
	icon: null,
};

CommandButton.propTypes = {
	icon: PropTypes.element,
	notifySuccess: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
		.isRequired,
	confirmTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
		.isRequired,
	confirmContent: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
		.isRequired,
	command: PropTypes.shape({
		name: PropTypes.string.isRequired,
		args: PropTypes.arrayOf(
			PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		),
		notify_args: PropTypes.shape({
			title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
			content: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			resource: PropTypes.string,
		}),
	}).isRequired,
};

export default CommandButton;
